import React, { useState } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

export const ProductSalesTrendChart = ({ startDate, endDate, data }) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState("revenue");

  const handleViewChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: "background.paper",
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {viewMode === "revenue" ? "Receita: " : "Quantidade: "}
            <strong>
              {viewMode === "revenue"
                ? `R$ ${payload[0].value.toFixed(2)}`
                : payload[0].value}
            </strong>
          </Typography>
          {payload[0].payload.topProduct && (
            <Typography variant="body2" color="text.secondary">
              Produto mais vendido:
              <br />
              <strong>{payload[0].payload.topProduct}</strong>
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <TrendingUpIcon
            size={24}
            color={theme.palette.primary.main}
            style={{ marginRight: 8 }}
          />
          <Typography variant="h6" fontWeight="600">
            Tendência de Vendas
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          size="small"
          aria-label="view mode"
        >
          <ToggleButton value="revenue" aria-label="revenue">
            Receita
          </ToggleButton>
          <ToggleButton value="quantity" aria-label="quantity">
            Quantidade
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {startDate ? new Date(startDate).toLocaleDateString() : ""} -{" "}
        {endDate ? new Date(endDate).toLocaleDateString() : ""}
      </Typography>

      <Box sx={{ height: 300, mt: 2 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getDate()}/${date.getMonth() + 1}`;
              }}
            />
            <YAxis
              tickFormatter={(value) =>
                viewMode === "revenue" ? `R$ ${value}` : value
              }
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey={viewMode === "revenue" ? "totalSales" : "totalQuantity"}
              name={
                viewMode === "revenue" ? "Receita Total" : "Quantidade Total"
              }
              stroke={theme.palette.primary.main}
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary">
          Média diária:{" "}
          <strong>
            {viewMode === "revenue"
              ? `R$ ${(
                  data.reduce((acc, item) => acc + item.totalSales, 0) /
                  data.length
                ).toFixed(2)}`
              : (
                  data.reduce((acc, item) => acc + item.totalQuantity, 0) /
                  data.length
                ).toFixed(0)}
          </strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Total:{" "}
          <strong>
            {viewMode === "revenue"
              ? `R$ ${data
                  .reduce((acc, item) => acc + item.totalSales, 0)
                  .toFixed(2)}`
              : data.reduce((acc, item) => acc + item.totalQuantity, 0)}
          </strong>
        </Typography>
      </Box>
    </Box>
  );
};
